import { useEffect, useRef } from "react";

// Sirena dell'allarme con il lettore audio del browser (elemento <audio>), senza librerie (titolare, 02/11/2026).
// Prima suonava con Web Audio (react-howler): sull'iPhone con iOS 26 restava muta anche dopo un tocco, e sull'app
// installata si bloccava tornando dal background. Il lettore classico funziona; sull'iPhone suona anche con il
// silenzioso acceso (scelta del titolare: un allarme non deve perdersi).
//
// Un solo lettore per tutta l'app: il telefono lo sblocca al primo tocco e da li' resta sbloccato. Suona finche'
// almeno una finestra di allarme lo chiede (piu' finestre insieme non sovrappongono la sirena).
const SRC = "./assets/alarm.wav";

let audio: HTMLAudioElement | null = null;
const wanted = new Set<number>();
let nextId = 1;

const player = () => {
  if (!audio) {
    audio = new Audio(SRC);
    audio.loop = true;
    audio.preload = "auto";
    audio.setAttribute("playsinline", "");
    // per le prove in locale, come ApiService
    if (window.location.hostname === "localhost") (window as any).__alarmAudio = audio;
  }
  return audio;
};

const sync = () => {
  const a = player();
  if (wanted.size > 0) {
    if (a.paused) {
      // senza un tocco il browser puo' rifiutare: riparte al primo tocco (unlock) o al ritorno in primo piano
      a.play().catch(() => {});
    }
  } else if (!a.paused) {
    a.pause();
    a.currentTime = 0;
  }
};

// Primo tocco o clic ovunque: il play() deve partire dentro il gesto, altrimenti l'iPhone non sblocca il lettore.
// Se in quel momento nessun allarme chiede la sirena, si fa partire muto e si ferma subito.
let unlocked = false;
const unlock = () => {
  if (unlocked) return;
  const a = player();
  if (wanted.size > 0) {
    // un play() partito prima del gesto (e destinato al rifiuto) lascia paused=false: si ricomincia da qui
    if (!a.paused) a.pause();
    a.play().then(() => (unlocked = true)).catch(() => {});
    return;
  }
  a.muted = true;
  a.play()
    .then(() => {
      unlocked = true;
      if (wanted.size === 0) {
        a.pause();
        a.currentTime = 0;
      }
    })
    .catch(() => {})
    .finally(() => {
      a.muted = false;
    });
};

if (typeof window !== "undefined") {
  // non pointerdown: sull'iPhone il tocco vale come gesto solo a touchend, e un play() rifiutato a pointerdown
  // arrivava dopo quello buono e lo fermava (02/11/2026)
  ["touchend", "click", "keydown"].forEach((ev) =>
    document.addEventListener(ev, unlock, { capture: true, passive: true })
  );
  // tornando all'app (schermo riacceso, app riaperta) la sirena riparte se c'e' ancora un allarme
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") sync();
  });
  window.addEventListener("pageshow", sync);
  // al tocco "Riattiva il suono" il play() parte dal gesto: sync anche dopo ogni tocco
  document.addEventListener("click", () => setTimeout(sync, 0), { capture: true, passive: true });
}

// La finestra dell'allarme chiede la sirena finche' playing e' vero
export const useAlarmSound = (playing: boolean) => {
  const id = useRef<number>(0);
  if (id.current === 0) id.current = nextId++;
  useEffect(() => {
    if (playing) wanted.add(id.current);
    else wanted.delete(id.current);
    sync();
  }, [playing]);
  useEffect(() => {
    const me = id.current;
    return () => {
      wanted.delete(me);
      sync();
    };
  }, []);
};
